/**
 * Light / dark switch in the footer (snippets/color-mode-toggle.liquid).
 *
 * Visitors start on "system" (their device's setting). Each click flips to the opposite of the colours on screen.
 * The preference goes to <html data-color-mode> and localStorage (`kastel-color-mode`); what is on screen goes to
 * <html data-theme>, which the CSS reads. snippets/color-mode-script.liquid applies both before the next page renders
 * and keeps data-theme in sync with the device while the preference is "system". A choice that matches the device
 * is stored as "system", so the site keeps following the device until the visitor picks otherwise.
 */
const STORAGE_KEY = 'kastel-color-mode';
const systemDark = window.matchMedia('(prefers-color-scheme: dark)');

class ColorModeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-color-mode-button]');
    if (!this.button) return;

    this.button.addEventListener('click', this.#toggle);
    systemDark.addEventListener('change', this.#syncLabel);
    this.#syncLabel();
  }

  disconnectedCallback() {
    systemDark.removeEventListener('change', this.#syncLabel);
  }

  /** Whether dark colours are currently on screen. */
  get isDark() {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  }

  #toggle = () => {
    const nextDark = !this.isDark;
    const mode = nextDark === systemDark.matches ? 'system' : nextDark ? 'dark' : 'light';
    const root = document.documentElement;

    root.setAttribute('data-color-mode', mode);
    root.setAttribute('data-theme', nextDark ? 'dark' : 'light');
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      // Storage can be unavailable (private browsing); the choice then lasts for this page only.
    }
    this.#syncLabel();
  };

  #syncLabel = () => {
    // The inline script updates data-theme on the same device change; read it after that listener has run.
    queueMicrotask(() => {
      this.button?.setAttribute('aria-label', this.isDark ? 'Switch to light mode' : 'Switch to dark mode');
    });
  };
}

if (!customElements.get('color-mode-toggle')) {
  customElements.define('color-mode-toggle', ColorModeToggle);
}
